/**
 * Fonte canônica de navegação do Inventário.
 *
 * Define em um único lugar a estrutura de tabs e subtabs usada pela página
 * de Inventário (`react/src/drheranca/pages/inventory/Home/Content.tsx`) e
 * pelo menu lateral (`react/src/drheranca/config.ts`). Mantém a regra do
 * SPEC §"Sincronia navegação Inventário (obrigatória)": qualquer inclusão,
 * remoção, renomeação ou reordenação de tabs/subtabs aqui é refletida
 * automaticamente nos dois locais.
 *
 * Para evitar dependência circular com `config.ts`, a flag
 * `hideInventoryDocumentsSection` é avaliada localmente a partir do mesmo
 * env var (`REACT_APP_HIDE_INVENTORY_DOCUMENTS_SECTION`).
 */
import type { ComponentType } from 'react';
import TreeIcon from '@mui/icons-material/AccountTreeTwoTone';
import GroupIcon from '@mui/icons-material/Group';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PrintIcon from '@mui/icons-material/Print';
import type { MenuItem } from 'src/core/types/appConfig';

const hideInventoryDocumentsSectionInternal =
  String(process.env.REACT_APP_HIDE_INVENTORY_DOCUMENTS_SECTION || '').toLowerCase() === 'true';

export interface InventoryNavSubtab {
  /** Identificador estável (não muda com renomeações de label). */
  key: string;
  /** Texto exibido ao usuário (sidebar + faixa de subtabs). */
  label: string;
}

export interface InventoryNavTab {
  /** Identificador estável (não muda com renomeações de label). */
  key: string;
  /** Texto exibido ao usuário (sidebar + faixa principal de tabs). */
  label: string;
  /** Ícone usado tanto no sidebar quanto na faixa de tabs. */
  icon: ComponentType<unknown>;
  /**
   * Subtabs em ordem canônica. Índices devem bater com o `invSubTab` esperado
   * pelos painéis em `Content.tsx`/`Report/Content.tsx`. Tabs sem `subtabs`
   * ou com 0/1 subtab não renderizam a faixa de subtabs nem itens filhos no
   * sidebar.
   */
  subtabs?: InventoryNavSubtab[];
}

/**
 * Constrói a lista canônica de tabs do Inventário.
 * Função (e não constante) para garantir que `hideInventoryDocumentsSection`
 * seja avaliado quando a navegação é montada, evitando issues de inicialização
 * em testes/SSR.
 */
export function getInventoryNavTabs(): InventoryNavTab[] {
  return [
    {
      key: 'tree',
      label: 'Árvore do Inventário',
      icon: TreeIcon,
    },
    {
      key: 'people',
      label: 'Pessoas',
      icon: GroupIcon,
      subtabs: [
        { key: 'decujus', label: 'De Cujus' },
        { key: 'inventariante', label: 'Inventariante' },
        { key: 'familiares', label: 'Familiares' },
      ],
    },
    {
      key: 'estate',
      label: 'Espólio',
      icon: HomeWorkIcon,
      subtabs: [
        { key: 'imoveis', label: 'Imóveis' },
        { key: 'veiculos', label: 'Veículos' },
        { key: 'investimentos', label: 'Saldos e Investimentos' },
        { key: 'participacoes', label: 'Participações Societárias' },
        { key: 'creditos', label: 'Créditos' },
        { key: 'criptoativos', label: 'Criptoativos' },
        { key: 'outros', label: 'Outros Bens' },
        { key: 'seguros', label: 'Seguros' },
        { key: 'exterior', label: 'Bens no Exterior' },
      ],
    },
    {
      key: 'debts',
      label: 'Dívidas',
      icon: RequestQuoteIcon,
      subtabs: [
        { key: 'financiamentos', label: 'Financiamentos de Bens' },
        { key: 'despesas', label: 'Dívidas e Despesas' },
      ],
    },
    {
      key: 'donations',
      label: 'Doações',
      icon: VolunteerActivismIcon,
    },
    {
      key: 'wills',
      label: 'Testamentos',
      icon: GavelIcon,
      subtabs: [
        { key: 'herdeiros', label: 'Herdeiros Testamentários' },
        { key: 'legados', label: 'Legados' },
      ],
    },
    {
      key: 'deed',
      label: 'Escritura',
      icon: DescriptionIcon,
    },
    {
      key: 'info-alerts',
      label: 'Alertas',
      icon: InfoOutlinedIcon,
    },
    {
      key: 'report',
      label: 'Relatório',
      icon: AssessmentIcon,
      subtabs: [
        { key: 'resumo', label: 'Resumo' },
        { key: 'partilha', label: 'Partilha' },
        { key: 'patrimonio', label: 'Patrimônio' },
        { key: 'qualificacao', label: 'Qualificação das Partes' },
        { key: 'bens', label: 'Bens do Espólio' },
        { key: 'dividas-doacoes', label: 'Dívidas e Doações' },
        { key: 'custos', label: 'Custos e Tributos' },
        ...(!hideInventoryDocumentsSectionInternal
          ? [{ key: 'exportacao', label: 'Exportação' }]
          : []),
      ],
    },
    {
      key: 'print',
      label: 'Impressão',
      icon: PrintIcon,
    },
  ];
}

/** Índice da tab principal "Impressão" (abre preview em nova aba). */
export function getInventoryPrintTabIndex(): number {
  return getInventoryNavTabs().findIndex((t) => t.key === 'print');
}

/** Quantidade total de tabs principais. */
export function getInventoryMainTabCount(): number {
  return getInventoryNavTabs().length;
}

/**
 * Subtab labels da tab `tabIndex`. Retorna `null` se a tab não tem subtabs
 * (ou tem apenas uma), para que a faixa secundária não seja renderizada.
 */
export function getInventorySubtabLabels(tabIndex: number): string[] | null {
  const tab = getInventoryNavTabs()[tabIndex];
  if (!tab?.subtabs || tab.subtabs.length <= 1) return null;
  return tab.subtabs.map((s) => s.label);
}

/**
 * Mapa `tabIndex → subtabLabels[]` (apenas tabs com 2+ subtabs).
 * Compatível com o antigo `INVENTORY_SUBTAB_LABELS` mantido em `Content.tsx`.
 */
export function getInventorySubtabLabelsMap(): Record<number, string[]> {
  const out: Record<number, string[]> = {};
  getInventoryNavTabs().forEach((tab, idx) => {
    if (tab.subtabs && tab.subtabs.length > 1) {
      out[idx] = tab.subtabs.map((s) => s.label);
    }
  });
  return out;
}

/**
 * Constrói os itens do menu lateral da seção "Inventário".
 * Os links usam o placeholder `:id` (resolvido em `SidebarMenu`) e
 * codificam `tab` e `subtab` na query string para sincronia exata com
 * a página de Inventário.
 */
export function buildInventorySidebarItems(): MenuItem[] {
  return getInventoryNavTabs().map((tab, idx) => {
    const baseLink = `/inventory/home/:id?tab=${idx}&subtab=0`;
    const item: MenuItem = {
      name: tab.label,
      link: baseLink,
      icon: tab.icon,
    };
    if (tab.subtabs && tab.subtabs.length > 1) {
      item.items = tab.subtabs.map((subtab, subIdx) => ({
        name: subtab.label,
        link: `/inventory/home/:id?tab=${idx}&subtab=${subIdx}`,
      }));
    }
    return item;
  });
}
